/**
 * @fileoverview Create Rule Page component for building margin and bid rules
 * This component provides a form interface for users to create new rules with conditions
 */

import { FormGroupHeader, InputField } from "@/components/shared";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { Card } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { ComparatorType, DecisionType, GroupOperator } from "@/graphql/__generated__/graphql";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { allowNullInputKeys, percentageInputKeys } from "../constants";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import useUserStore from "@/store/shield";
import { useCreateRuleMutationHook } from "@/hooks";
import { convertDecimalToBp } from "@/utils/helpers";
import { createRuleSchema } from "./CreateRuleSchema";
import ConditionComponent from "./ConditionComponent";

/**
 * CreateRulePage component for creating new margin or bid rules
 *
 * Provides a form with fields for rule name, description, and conditions.
 * Automatically determines rule type (margin or bid) based on the current URL path.
 * Allows adding and removing conditions dynamically.
 *
 * @returns {JSX.Element} The rendered create rule page component
 */
function CreateRulePage() {
  const { t } = useTranslation(["translation", "common", "schemas"]);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const organizationId: number | undefined = useUserStore((state) => state.orgId);

  const ruleType = pathname.includes("marginrules") ? DecisionType.Margin : DecisionType.Bid;

  const { createRuleMutation, loading: createRuleLoading } = useCreateRuleMutationHook(ruleType);

  const [conditionsKey, setConditionsKey] = useState(0);
  const { CreateRuleSchema } = createRuleSchema(t);

  // Define the CreateRuleInput type based on the schema
  type CreateRuleInput = z.infer<typeof CreateRuleSchema>;

  /**
   * Form instance for the rule creation form
   * Initialized with default values and validation schema
   */
  const form = useForm<CreateRuleInput>({
    resolver: zodResolver(CreateRuleSchema),
    defaultValues: {
      name: "",
      description: "",
      decisionType: ruleType,
      rootGroup: {
        operator: GroupOperator.And,
        conditions: [{}], // Empty condition
        childGroups: []
      }
    },
    mode: "onChange",
  });

  /**
   * Handles form submission
   * Processes the form data, converts values as needed, and submits to the API
   *
   * @param {CreateRuleInput} data - The form data to submit
   */
  const onSubmit = async (data: CreateRuleInput) => {
    // Create a deep copy of the rootGroup to avoid mutating the original data
    const processedRootGroup = JSON.parse(JSON.stringify(data.rootGroup));

    // Process all conditions in the rootGroup
    if (processedRootGroup.conditions && processedRootGroup.conditions.length > 0) {
      processedRootGroup.conditions = processedRootGroup.conditions.map((condition: any) => {
        // Check if the condition's computationType is in the percentageInputKeys array
        if (condition.computationType && percentageInputKeys.includes(condition.computationType)) {
          // Check if the condition's computationType is in the allowNullInputKeys array and the operator is IsNull to explicitly set the value to "null"
          const isNullCondition = allowNullInputKeys.includes(condition.computationType) && condition.operator === ComparatorType.IsNull;
          // Convert the value from decimal to basis points, unless value is "null"
          return {
            ...condition,
            value: isNullCondition ? "null" : String(convertDecimalToBp(Number(condition.value)))
          };
        }
        return condition;
      });
    }

    const variables = {
      organizationId: organizationId!,
      name: data.name,
      decisionType: data.decisionType,
      rootGroup: processedRootGroup,
      description: data.description
    };

    await createRuleMutation({ variables });

    // navigate back to the previous page
    navigate(-1);
  };

  /**
   * Configuration for action buttons in the form header
   * Includes back and create buttons with their respective actions
   */
  const actionButtons: ActionButtonConfig[] = [
    {
      text: t("common:buttons.back"),
      variant: "outline",
      type: "button",
      actionFn: () => {
        navigate(-1);
      },
      disabled: createRuleLoading
    },
    {
      text: t("common:buttons.create"),
      variant: "default",
      type: "submit",
      actionFn: () => {
        form.handleSubmit(onSubmit)();
      },
      loading: createRuleLoading
    },
  ];

  /**
   * Adds a new condition to the specified group
   *
   * @param {string} path - The path to the group where the condition should be added
   */
  const addCondition = (path: string) => {
    // Use type assertion to handle the dynamic path
    const currentGroup = form.getValues(path as any);
    const updatedConditions = [...currentGroup.conditions, {}];

    // Use type assertion for the dynamic path
    form.setValue(`${path}.conditions` as any, updatedConditions);
  };

  /**
   * Handles deletion of a condition at the specified index
   *
   * @param {number} index - The index of the condition to delete
   */
  const handleDeleteCondition = (index: number) => {
    // Get the current conditions array
    const currentConditions = [...form.getValues("rootGroup.conditions" as any)];
    // Remove the condition at the specific index
    currentConditions.splice(index, 1);
    // Update the form state with the modified array
    form.setValue("rootGroup.conditions" as any, currentConditions);
    // Force re-render by updating the key
    setConditionsKey(prev => prev + 1);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormGroupHeader
          className="pr-4 pl-2"
          title={ruleType === DecisionType.Margin ? t("settings.rulebuilder.create.margin.title") : t("settings.rulebuilder.create.bid.title")}
          description={ruleType === DecisionType.Margin ? t("settings.rulebuilder.create.margin.description") : t("settings.rulebuilder.create.bid.description")}
          actionButtons={actionButtons}
        />
        <div className="flex flex-col gap-4 pr-4 pl-2 pb-[300px]">
          <div className="flex flex-col gap-4">
            <InputField
              name="name"
              label={t("settings.rulebuilder.create.inputs.name.label")}
              placeholder={t("settings.rulebuilder.create.inputs.name.placeholder")}
              form={form}
            />
            <InputField
              name="description"
              label={t("settings.rulebuilder.create.inputs.description.label")}
              placeholder={t("settings.rulebuilder.create.inputs.description.placeholder")}
              form={form}
              className="w-full"
              showOptional={true}
            />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-400">{t("settings.rulebuilder.conditions")}</p>
            <Separator className="my-2" />
            <Card className="flex flex-col p-4" key={conditionsKey}>
              {form.watch("rootGroup.conditions").map((_, index) => (
                <ConditionComponent
                  key={index}
                  form={form}
                  index={index}
                  onDelete={index > 0 ? () => handleDeleteCondition(index) : undefined}
                  ruleType={ruleType}
                  t={t}
                  path="rootGroup"
                />
              ))}

              <div className="mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => addCondition("rootGroup")}
                  type="button"
                >
                  <PlusIcon className="mr-2 w-4 h-4" />
                  {t("settings.rulebuilder.create.btnnewcondition")}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </Form>
  );
}

export default CreateRulePage;
